import { useMemo } from 'react';
import { get } from 'lodash';
import * as R from 'remeda';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { User } from '@rossum/api-client/users';
import PaperSection from '@rossum/components/paper-section/PaperSection';
import { IconInfoCircle } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Stack,
  SvgIcon,
  Switch,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { usePatchUser } from '../../../business/users/usePatchUser';
import RadioGroupControl from '../../../components/ReactHookForm/controls/RadioGroupControl';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import SelectItems from '../../../components/ReactHookForm/SelectItems';
import ValidationInput from '../../../components/ReactHookForm/ValidationInput';
import HiddingButton from '../../../components/UI/HiddingButton';
import { isDevFeatureEnabled } from '../../../constants/dev';
import { docsLinks } from '../../../constants/values';
import {
  getLocaleOptions,
  IntlLocale,
  LOCALE_DEFAULT_SENTINEL,
} from '../../../i18n/config';
import { linebreak, link } from '../../../lib/formaterValues';
import {
  getRoleOptions,
  getRoleUrl,
  isApproverRole,
} from '../../../redux/modules/groups/helpers';
import { groupsSelector } from '../../../redux/modules/groups/selectors';
import { isSSOEnable } from '../../../redux/modules/organization/selectors';
import { additionalLocalesEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import { splitFullName } from '../../../redux/modules/user/helpers';
import { userSelector } from '../../../redux/modules/user/selectors';
import { PASSWORD, SSO } from '../../../redux/modules/users/values';
import { GroupRole } from '../../../types/group';
import { AuthType, RossumLabConfig, UiSettings } from '../../../types/user';
import { FeatureToggles } from '../../feature-enablement-ui/FeatureToggles';
import {
  isFeatureEnabled,
  isFeatureGloballyEnabled,
} from '../../feature-enablement/featureEnablement';
import {
  labFeatures,
  labFeaturesList,
} from '../../feature-enablement/features';
import { approvalWorkflowsFeatureSelector } from '../../pricing/selectors';
import { useUser } from '../../user-panel/hooks/useUser';
import { RoleSelect } from '../components/RoleSelect';
import { RoleTooltip } from '../components/RoleTooltip';

// Trigger CI
const ORG_GROUP_ADMIN: GroupRole = 'organization_group_admin';

type InfoProps = {
  selectedUser: User;
};

const userValidationSchema = (intl: IntlShape) =>
  yup.object().shape({
    name: yup.string().required(
      intl.formatMessage({
        id: 'containers.settings.users.addUser.name.error',
      })
    ),
    role: yup.string().required(
      intl.formatMessage({
        id: 'containers.settings.users.addUser.role.error',
      })
    ),
    oidcId: yup
      .string()
      .nullable()
      .when('authType', {
        is: SSO,
        then: yup
          .string()
          .nullable()
          .max(
            255,
            intl.formatMessage({
              id: 'containers.settings.users.addUser.oidcId.error',
            })
          ),
      }),
    canApprove: yup.boolean().optional(),
  });

type FormParams = {
  authType: AuthType;
  oidcId: string | null;
  locale: IntlLocale | typeof LOCALE_DEFAULT_SENTINEL;
  name: string;
  role: string;
  canApprove: boolean;
};

export const Info = ({ selectedUser }: InfoProps) => {
  const intl = useIntl();
  const { mutate: patchUser } = usePatchUser();

  const enableApproverRole = useSelector(approvalWorkflowsFeatureSelector);
  const groups = useSelector(groupsSelector);
  const isSSOEnabled = useSelector(isSSOEnable);
  const additionalLocalesEnabled = useSelector(
    additionalLocalesEnabledSelector
  );
  const localeOptions = useMemo(
    () => getLocaleOptions({ additionalLocalesEnabled }),
    [additionalLocalesEnabled]
  );

  const defaultLocaleLabel = intl.formatMessage({
    id: 'containers.settings.users.addUser.locale.default',
  });

  type LocaleFieldValue = IntlLocale | typeof LOCALE_DEFAULT_SENTINEL;

  const localeOptionsWithDefault = useMemo<Array<{ value: LocaleFieldValue }>>(
    () => [
      { value: LOCALE_DEFAULT_SENTINEL },
      ...localeOptions.map(({ value }) => ({ value })),
    ],
    [localeOptions]
  );
  const userRoleUrl = getRoleUrl(selectedUser.groups, groups);
  const isApprover = isApproverRole(selectedUser.groups, groups);
  const roleOptions = getRoleOptions(groups, intl);

  const roleOptionsWithFlag = roleOptions.filter(role =>
    enableApproverRole || isApprover ? role : role.name !== 'approver'
  );

  const { data: loggedInUser } = useUser();
  const loggedInUserId = loggedInUser?.id;

  // TODO: parse user uiSettings via proper parser
  const locale: LocaleFieldValue =
    'locale' in (selectedUser.uiSettings as UiSettings) &&
    (selectedUser.uiSettings as UiSettings).locale
      ? ((selectedUser.uiSettings as UiSettings).locale as IntlLocale)
      : LOCALE_DEFAULT_SENTINEL;

  const role = get(
    roleOptionsWithFlag.find(role => role.value === userRoleUrl),
    'name',
    ''
  );
  const name = R.pipe(
    [selectedUser.firstName, selectedUser.lastName],
    R.filter(R.isTruthy),
    R.join(' ')
  );

  const currentUser = useSelector(userSelector);

  const { authType, oidcId = null, url: selectedUserUrl } = selectedUser;

  const {
    handleSubmit,
    watch,
    control,
    formState: { isValid },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      authType,
      oidcId,
      locale,
      name,
      role,
      canApprove: isApprover,
    },
    resolver: yupResolver(userValidationSchema(intl)),
  });

  const rawWatchValues = watch();
  const watchValues = {
    ...rawWatchValues,
    canApprove: rawWatchValues.canApprove || rawWatchValues.role === 'approver',
  };

  const isUserOrgGroupAdmin = role === ORG_GROUP_ADMIN;
  const isRoleSelectDisabled =
    loggedInUserId === selectedUser.id || isUserOrgGroupAdmin;

  const ssoActive = watchValues.authType === SSO;

  const canSubmit =
    !R.isDeepEqual(
      ssoActive ? watchValues : R.omit(watchValues, ['oidcId']),
      ssoActive
        ? {
            name,
            locale,
            role,
            authType,
            oidcId,
            canApprove: isApprover,
          }
        : {
            name,
            locale,
            role,
            authType,
            canApprove: isApprover,
          }
    ) && isValid;

  const onSubmit = ({
    name: newName,
    locale: newLocale,
    role: newRole,
    authType: newAuthType,
    oidcId: newOidcId,
  }: FormParams) => {
    if (!canSubmit) return null;

    const { firstName, lastName } = splitFullName(newName);
    return patchUser({
      userId: selectedUser.id,
      payload: {
        firstName,
        lastName,
        groups: watch().canApprove
          ? [
              get(
                roleOptions.find(option => option.name === newRole),
                'value',
                ''
              ),
              get(
                roleOptions.find(option => option.name === 'approver'),
                'value',
                ''
              ),
            ]
          : [
              get(
                roleOptions.find(option => option.name === newRole),
                'value',
                ''
              ),
            ],
        ...(newLocale && {
          uiSettings: {
            locale: newLocale === LOCALE_DEFAULT_SENTINEL ? null : newLocale,
          },
        }),
        authType: newAuthType,
        oidcId: newAuthType === SSO ? newOidcId : oidcId,
      },
    });
  };

  const displayLogoutWarning =
    watchValues.authType !== authType && currentUser.url === selectedUserUrl;

  const visibleLabFeatures = useMemo(
    () =>
      labFeaturesList.filter(feature => isFeatureGloballyEnabled(feature.id)),
    []
  );

  // TODO: parse user uiSettings via proper parser
  const userLabConfig = useMemo(() => {
    if ('labConfig' in (selectedUser.uiSettings as UiSettings)) {
      return (selectedUser.uiSettings as UiSettings)
        .labConfig as RossumLabConfig;
    }

    return {};
  }, [selectedUser.uiSettings]);

  const featureConfig = useMemo(() => {
    return R.mapValues(labFeatures, feature => ({
      enabled: isFeatureEnabled(feature.id, userLabConfig),
    }));
  }, [userLabConfig]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-page-title="user-info">
      <Stack spacing={4}>
        <PaperSection
          title={intl.formatMessage({
            id: 'containers.settings.queues.email.general',
          })}
        >
          <Stack direction="row" alignItems="center">
            <Typography sx={{ width: 115 }}>
              <FormattedMessage id="containers.settings.users.addUser.email.label" />
            </Typography>
            <Stack sx={{ width: 300 }}>
              {selectedUser.email || selectedUser.username}
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Typography sx={{ width: 115 }}>
              <FormattedMessage id="containers.settings.users.addUser.name.label" />
            </Typography>
            <Stack sx={{ width: 300 }}>
              <TextFieldControl
                ControllerProps={{ control, name: 'name' }}
                placeholder={intl.formatMessage({
                  id: 'containers.settings.users.addUser.name.placeholder',
                })}
                FieldLabelProps={{ layout: 'none' }}
                inputProps={{
                  'data-cy': 'user-info-name-input',
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Typography sx={{ width: 115 }}>
              <FormattedMessage id="containers.settings.users.addUser.locale.label" />
            </Typography>
            <Stack sx={{ width: 300 }}>
              <SelectItems<FormParams, LocaleFieldValue>
                withDescription={false}
                name="locale"
                control={control}
                options={localeOptionsWithDefault}
                getLabel={selectedValue =>
                  selectedValue === LOCALE_DEFAULT_SENTINEL
                    ? defaultLocaleLabel
                    : (localeOptions.find(
                        ({ value }) => value === selectedValue
                      )?.label ?? selectedValue)
                }
                placeholderKey="containers.settings.users.addUser.locale.placeholder"
                dataCy="locale-drop-down"
              />
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Stack spacing={1} direction="row" sx={{ width: 115 }}>
              <Typography>
                <FormattedMessage id="containers.settings.users.addUser.role.label" />
              </Typography>
              <RoleTooltip
                roles={roleOptionsWithFlag.map(roleOption => roleOption.name)}
              />
            </Stack>
            <Stack sx={{ width: 300, opacity: isRoleSelectDisabled ? 0.5 : 1 }}>
              <Tooltip
                title={
                  isRoleSelectDisabled ? (
                    <FormattedMessage
                      id={
                        isUserOrgGroupAdmin
                          ? 'containers.settings.users.roleSelectDisabled.isGroupAdmin'
                          : 'containers.settings.users.roleSelectDisabled.isOwnRole'
                      }
                      values={{
                        linebreak,
                        link: link('mailto:support@rossum.ai'),
                      }}
                    />
                  ) : (
                    ''
                  )
                }
              >
                <Stack>
                  <RoleSelect<FormParams>
                    name="role"
                    control={control}
                    roleOptions={roleOptionsWithFlag}
                    getErrorMessage={(_, { message }) => message ?? ''}
                    disabled={isRoleSelectDisabled}
                  />
                </Stack>
              </Tooltip>
            </Stack>
          </Stack>
          {enableApproverRole && (
            <Stack direction="row" alignItems="center">
              <Typography sx={{ width: 115 }}>
                {intl.formatMessage({
                  id: 'containers.settings.users.addUser.canApprove',
                })}
              </Typography>
              <Stack sx={{ width: 300 }}>
                <Controller
                  control={control}
                  name="canApprove"
                  render={({ field }) => (
                    <Switch
                      data-cy="can-approve-user-settings-togle"
                      checked={field.value || watchValues.role === 'approver'}
                      disabled={watchValues.role === 'approver'}
                      onChange={(_, value) => field.onChange(value)}
                    />
                  )}
                />
              </Stack>
            </Stack>
          )}
        </PaperSection>
        {isDevFeatureEnabled('labs-config') ? (
          <PaperSection
            title={intl.formatMessage({
              id: 'containers.settings.users.labFeatures',
            })}
          >
            <FeatureToggles
              features={visibleLabFeatures}
              config={featureConfig}
              readonly
            />
          </PaperSection>
        ) : null}
        {isSSOEnabled && (
          <PaperSection
            title={intl.formatMessage({
              id: 'containers.settings.users.authentication',
            })}
          >
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center">
                <Typography sx={{ width: 115 }}>
                  <FormattedMessage id="containers.settings.users.addUser.authType" />
                </Typography>
                <Stack sx={{ width: 300 }}>
                  <RadioGroupControl
                    ControllerProps={{ control, name: 'authType' }}
                    getOptionLabel={option => (
                      <Stack mt="18px">
                        <Typography variant="body2" fontWeight="bold">
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.tooltip}
                        </Typography>
                      </Stack>
                    )}
                    getOptionValue={option => option.value}
                    options={([SSO, PASSWORD] as const).map(value => ({
                      value,
                      label: intl.formatMessage({
                        id: `containers.settings.users.authType.${value}`,
                      }),
                      tooltip: intl.formatMessage({
                        id: `containers.settings.users.authType.${value}.tooltip`,
                      }),
                    }))}
                  />
                </Stack>
              </Stack>
              {ssoActive && (
                <Stack direction="row" alignItems="center">
                  <Stack
                    spacing={1}
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ width: 115, pr: 2 }}
                  >
                    <Typography>
                      <FormattedMessage id="containers.settings.users.addUser.oidcId.label" />
                    </Typography>
                    <Tooltip
                      title={
                        <FormattedMessage
                          id="containers.settings.users.addUser.oidcId.tooltip"
                          values={{ link: link(`${docsLinks.sso}`) }}
                        />
                      }
                    >
                      <SvgIcon fontSize="medium" sx={{ mr: 1 }}>
                        <IconInfoCircle />
                      </SvgIcon>
                    </Tooltip>
                  </Stack>
                  <Stack sx={{ width: 300 }}>
                    <ValidationInput
                      control={control}
                      getErrorMessage={(_, { message }) => message ?? ''}
                      name="oidcId"
                      placeholder={intl.formatMessage({
                        id: 'containers.settings.users.addUser.oidcId.placeholder',
                      })}
                    />
                  </Stack>
                </Stack>
              )}
              {displayLogoutWarning && (
                <Alert
                  severity="warning"
                  variant="filled"
                  sx={{ alignItems: 'center' }}
                >
                  <Typography variant="body1">
                    {intl.formatMessage({
                      id: 'containers.settings.users.user.logoutWarning',
                    })}
                  </Typography>
                </Alert>
              )}
            </Stack>
          </PaperSection>
        )}
        <Stack alignItems="center">
          <HiddingButton
            hideCondition={!canSubmit}
            messageId="containers.settings.fields.save"
            type="submit"
          />
        </Stack>
      </Stack>
    </form>
  );
};
